import { Button, Tooltip } from 'antd';
import { useMemo } from 'react';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import Authorities, { AuthoritiesType } from '@/types/Authority';
import { ButtonProps } from 'antd/es/button';

interface Props extends ButtonProps {
  customerId?: number;
  vehicleId?: number;
  mode: 'create' | 'edit';
  onClick: () => void;
  label?: React.ReactNode;
  variant?: 'default' | 'primary';
  requireCustomer?: boolean;
  customPermission?: AuthoritiesType;
  customMessages?: string[];
  tooltipEnabled?: string;
}

export default function VehicleActionButton({
  customerId,
  vehicleId,
  onClick,
  mode,
  icon,
  label,
  variant = 'default',
  block = false,
  requireCustomer = true,
  customMessages = [],
  customPermission,
  tooltipEnabled,
  ...props
}: Props) {
  const { isDisabled, messages } = useMemo(() => {
    const validationMessages: string[] = [];

    validationMessages.push(...customMessages.filter((msg) => !!msg));

    if (requireCustomer && !customerId) {
      validationMessages.push('Cliente não informado');
    }

    if (mode === 'edit' && !vehicleId) {
      validationMessages.push('Veículo não selecionado');
    }

    const permission: AuthoritiesType =
      customPermission ||
      (mode === 'edit'
        ? Authorities.REGISTER_CUSTOMER_VEHICLE_EDIT
        : Authorities.REGISTER_CUSTOMER_VEHICLE_ADD);

    if (!hasPermissions([permission])) {
      validationMessages.push(
        customPermission
          ? 'Sem permissão para executar ação'
          : mode === 'edit'
          ? 'Sem permissão para editar veículo'
          : 'Sem permissão para cadastrar veículo',
      );
    }

    return {
      isDisabled: validationMessages.length > 0,
      messages: validationMessages,
    };
  }, [
    customerId,
    vehicleId,
    mode,
    requireCustomer,
    customPermission,
    customMessages,
  ]);

  const tooltipTitle = isDisabled ? (
    <ul style={{ margin: 0, paddingLeft: 16 }}>
      {messages.map((message, index) => (
        <li key={index}>{message}</li>
      ))}
    </ul>
  ) : (
    tooltipEnabled ||
    label ||
    (mode === 'edit' ? 'Editar veículo' : 'Cadastrar veículo')
  );

  return (
    <Tooltip title={tooltipTitle}>
      <Button
        {...props}
        type={variant}
        block={block}
        icon={icon}
        disabled={isDisabled}
        onClick={() => {
          if (!isDisabled) {
            onClick();
          }
        }}
      >
        {label}
      </Button>
    </Tooltip>
  );
}
